import { FC, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import styles from "@/components/anchorData/anchorData.module.css";
import { useTranslation } from "react-i18next";
import { api } from "@/helper/api";
import { ANCHORDATA_VIRTUAL_CATEGORY_MISSING_REQUIRED } from "@/helper/anchorDataCategories";
import CategoryTabPanel, {
  ContractSetupTab,
} from "@/components/anchorData/categoryTabs";
import { GenericAccordion } from "@/components/anchorData/genericAccordion";
import AnchorDataForm, {
  AnchorField,
} from "@/components/anchorData/anchorDataForm";
import { FormProvider, useForm } from "react-hook-form";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { Checkbox, FormControlLabel, LinearProgress } from "@mui/material";
import { InputWithRef } from "@/components/forms/inputsWithRef/inputWithRef";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import useConfirmOnLeave from "@/helper/customHooks/useConfirmOnLeave";
import dayjs, { Dayjs } from "dayjs";
import ExportMenu, {
  ExportMenuItem,
} from "@/components/appointmentRequest/exportMenu";
import { useRegisterUnsavedChanges } from "@/context/unsavedChangesContext";
import { getAnchorDataBulkCreateUpdateErrors } from "@/helper/anchorDataBulkCreateUpdate.utils";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import CompaniesMissingRequiredButton from "@/components/anchorData/companiesMissingRequiredButton";
import type { CompanyMissingRequiredRow } from "@/components/anchorData/useCompaniesMissingRequiredAnchorData";
import { setSubjectSearch } from "@/components/state/subjectSearch/subjectSearchSlice";
import { toCompanyOption } from "@/helper/quickSearchSelection";
import type { CompanyQuickSearchResult } from "@/types/QuickSearch";

interface AnchorDataListProps {
  subdomain: string;
}

const AnchorDataList: FC<AnchorDataListProps> = ({ subdomain }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedCompanyIdRef = useRef(
    subdomain === "admin" ? searchParams.get("company") : null,
  );
  const [isResolvingCompany, setIsResolvingCompany] = useState(
    Boolean(linkedCompanyIdRef.current),
  );
  const [subjectId, setSubjectId] = useState<string | null>(null);
  const [category, setCategory] = useState<string | false>(() => {
    // only the virtual tab is deep-linkable; real categories stay
    // non-linkable to keep the param surface minimal
    const fromUrl = searchParams.get("category");
    return fromUrl === ANCHORDATA_VIRTUAL_CATEGORY_MISSING_REQUIRED ||
      linkedCompanyIdRef.current
      ? ANCHORDATA_VIRTUAL_CATEGORY_MISSING_REQUIRED
      : false;
  });
  const [categoryData, setCategoryData] = useState<any[] | null>(null);
  const [contractTabs, setContractTabs] = useState<ContractSetupTab[]>([]);
  const [hasMissingRequired, setHasMissingRequired] = useState<boolean | null>(
    null
  );
  const missingRequiredRequestIdRef = useRef(0);
  const contractTabsRequestIdRef = useRef(0);
  const anchorDataRequestIdRef = useRef(0);
  const selectionRef = useRef({ subjectId, category });
  selectionRef.current = { subjectId, category };
  const [accordionItems, setAccordionItems] = useState<
    { title: string; content: React.ReactNode }[]
  >([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchForValues, setSearchForValues] = useState<boolean>(false);
  const [openAccordionIndices, setOpenAccordionIndices] = useState<number[]>(
    []
  );
  const [defaultValues, setDefaultValues] = useState<any | null>(null);
  const [dataLoadId, setDataLoadId] = useState<number>(0);
  const { handleError, errorModal } = useErrorModal();
  const [successForNotification, setSuccessForNotification] =
    useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();

  const methods = useForm<any>({
    mode: "onChange",
    defaultValues: defaultValues ?? {},
    shouldUnregister: true,
  });

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const anchorDataSubjectRef = useRef(null);
  const { showConfirmationModalAsync } = useConfirmationModal();

  useEffect(() => {
    const companyId = linkedCompanyIdRef.current;
    if (!companyId) return;
    const controller = new AbortController();
    dispatch({ type: "clearSubjectSearch" });
    api.genericApiRequest({
      method: "get",
      entity: "company",
      entityId: companyId,
      parametersToRender: { subject: "true" },
      shouldNotImpersonate: true,
      config: { signal: controller.signal },
      successHandler: ({
        data,
      }: {
        data: CompanyQuickSearchResult & { subject?: { id?: string } };
      }) => {
        if (controller.signal.aborted) return;
        const companySubjectId = data.subject?.id || data.subject_id;
        if (companySubjectId) {
          dispatch(
            setSubjectSearch(
              toCompanyOption({ ...data, subject_id: companySubjectId }),
            ),
          );
          setSubjectId(companySubjectId);
        } else {
          handleError(t("anchor_data_company_unavailable"));
        }
        setIsResolvingCompany(false);
      },
      failHandler: (error: unknown) => {
        if (controller.signal.aborted) return;
        handleError(error);
        setIsResolvingCompany(false);
      },
    });
    return () => controller.abort();
  }, []);

  const formValues = methods.watch();
  const changedValues = getChangedFormFields(
    formValues,
    methods.formState.defaultValues
  );

  const hasUnsavedChanges = Object.keys(changedValues).length > 0;

  useRegisterUnsavedChanges("AnchorDataList", hasUnsavedChanges);
  useConfirmOnLeave(hasUnsavedChanges, "unsaved_formChanges_confirm");

  useEffect(() => {
    if (
      searchParams.get("category") ===
      ANCHORDATA_VIRTUAL_CATEGORY_MISSING_REQUIRED
    ) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("category");
      setSearchParams(nextParams, { replace: true });
    }
    // runs once on mount: consume the deep-link param, then keep the URL clean
  }, []);

  useEffect(() => {
    anchorDataRequestIdRef.current += 1;
    setCategoryData(null);
    setDefaultValues(null);
    if (!subjectId || !category) {
      methods.reset({});
      return;
    }

    getAnchorData();
  }, [subjectId, category]);

  useEffect(() => {
    setContractTabs([]);
    if (subjectId) {
      loadContractTabs(subjectId).catch((error) => {
        if (selectionRef.current.subjectId === subjectId) handleError(error);
      });
    }
    return () => {
      contractTabsRequestIdRef.current += 1;
    };
  }, [subjectId]);

  useEffect(() => {
    setHasMissingRequired(null);
    if (
      subjectId &&
      category !== ANCHORDATA_VIRTUAL_CATEGORY_MISSING_REQUIRED
    ) {
      checkMissingRequired(subjectId);
    }
    return () => {
      missingRequiredRequestIdRef.current += 1;
    };
  }, [subjectId]);

  useEffect(() => {
    if (
      hasMissingRequired === false &&
      category === ANCHORDATA_VIRTUAL_CATEGORY_MISSING_REQUIRED
    ) {
      setCategory(false);
    }
  }, [hasMissingRequired, category]);

  //building and fill accordions
  useEffect(() => {
    if (!categoryData) return;

    const newItems: {
      title: string;
      content: React.ReactNode;
      hidden?: boolean;
    }[] = [];
    const newOpenIndices: number[] = [];
    const query = searchQuery?.toLowerCase().trim() ?? "";
    const searching = query.length > 0;
    const isContractTab = contractTabs.some((tab) => tab.code === category);

    const subcategories: string[] = Array.from(
      new Set(
        categoryData.map((item: any) => item?.subcategory).filter(Boolean)
      )
    );

    subcategories.forEach((sub: string) => {
      const subItems = categoryData.filter(
        (item: any) => item?.subcategory === sub
      );

      const fields = subItems.map((subitem: any) => {
        const name = subitem?.name ?? "";
        const label = subitem?.label ?? "";

        const normalValue = subitem?.anchor_data?.value;
        const inheritedValue = subitem?.anchor_data_inherited?.value;

        const hasInheritedValues =
          !!subitem?.anchor_data_inherited &&
          Object.keys(subitem.anchor_data_inherited).length > 0;

        const isValueInherited =
          (normalValue === undefined || normalValue === null) &&
          inheritedValue !== undefined &&
          inheritedValue !== null;

        const value =
          normalValue !== undefined && normalValue !== null
            ? normalValue
            : inheritedValue ?? "";

        const inheritedFrom =
          hasInheritedValues && subitem?.anchor_data_inherited?.subject
            ? {
                id: subitem.anchor_data_inherited.subject.id,
                name: subitem.anchor_data_inherited.subject.name,
              }
            : null;

        const valueStr = String(value).toLowerCase();

        const matches =
          !searching ||
          name.toLowerCase().includes(query) ||
          label.toLowerCase().includes(query) ||
          (searchForValues && valueStr.includes(query));

        return {
          name,
          label,
          required: subitem?.required === true,
          requiredForActivation: subitem?.required_for_activation === true,
          highlightMissing: isContractTab && subitem?.missing_required === true,
          type: subitem?.type,
          value,
          options: subitem?.allowed_value_set ?? [],
          is_calculated: subitem.is_calculated,
          editable: !subitem.is_calculated,
          hiddenBySearch: searching ? !matches : false,
          valid_from:
            subitem?.anchor_data?.valid_from ??
            subitem?.anchor_data_inherited?.valid_from,
          subdomain: subdomain,
          typeMapping: subitem?.type_mapping,
          isValueInherited,
          inheritedFrom,
          hasInheritedValues,
          inheritedValue,
          modifiedBy: subitem?.anchor_data?.modified_by ?? null,
          modifiedOn: subitem?.anchor_data?.modified_on ?? null,
        };
      });

      const hasVisibleFields = fields.some((f) => !f.hiddenBySearch);
      if (
        (searching && hasVisibleFields) ||
        (!searching && fields.some((field) => field.highlightMissing))
      ) {
        newOpenIndices.push(newItems.length);
      }
      if (fields.length > 0) {
        newItems.push({
          title: sub,
          content: <AnchorDataForm fields={fields} mode="form" />,
          hidden: searching && !hasVisibleFields,
        });
      }
    });

    setAccordionItems(newItems);
    setOpenAccordionIndices(newOpenIndices);
  }, [
    categoryData,
    searchQuery,
    searchForValues,
    methods,
    category,
    contractTabs,
  ]);

  const handleCompanySelect = (selectedOption: any) => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      if (
        searchParams.has("company") &&
        selectedOption.companyId &&
        searchParams.get("company") !== selectedOption.companyId
      ) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("company", selectedOption.companyId);
        setSearchParams(nextParams, { replace: true });
      }
      if (
        selectedOption.value !== subjectId &&
        contractTabs.some((tab) => tab.code === category)
      ) {
        setCategory(false);
      }
      setSubjectId(selectedOption.value);
    }
  };
  const handleSubjectReset = () => {
    if (searchParams.has("company")) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("company");
      setSearchParams(nextParams, { replace: true });
    }
    if (contractTabs.some((tab) => tab.code === category)) {
      setCategory(false);
    }
    setSubjectId(null);
  };
  const handleMissingRequiredCompanySelect = async (
    company: CompanyMissingRequiredRow
  ): Promise<boolean> => {
    if (
      company.subject_id === subjectId &&
      category === ANCHORDATA_VIRTUAL_CATEGORY_MISSING_REQUIRED
    ) {
      return true;
    }
    if (
      hasUnsavedChanges &&
      !(await showConfirmationModalAsync(t("unsaved_formChanges_confirm")))
    ) {
      return false;
    }

    setCategory(ANCHORDATA_VIRTUAL_CATEGORY_MISSING_REQUIRED);
    setSubjectId(company.subject_id);
    dispatch(setSubjectSearch(toCompanyOption(company)));
    return true;
  };
  const handleCategoryChange = (
    event: React.SyntheticEvent,
    newValue: string
  ) => {
    setCategory(newValue);
  };

  const getAnchorData = async () => {
    const requestId = ++anchorDataRequestIdRef.current;
    const additionalUrlParameter: any = {};
    additionalUrlParameter["include_anchor_data"] = true;
    additionalUrlParameter["subject"] = subjectId;
    if (category === ANCHORDATA_VIRTUAL_CATEGORY_MISSING_REQUIRED) {
      additionalUrlParameter["missing_required"] = true;
    } else if (contractTabs.some((tab) => tab.code === category)) {
      additionalUrlParameter["contract"] = category;
    } else {
      additionalUrlParameter["category"] = category;
    }

    try {
      const res: any = await new Promise<any>((resolve, reject) => {
        api.genericApiRequest({
          method: "get",
          entity: "anchorDataTemplate",
          parametersToRender: additionalUrlParameter,
          successHandler: (res: any) => {
            resolve(res);
          },
          failHandler: (error: any) => {
            reject(error);
            console.log(error);
          },
        });
      });
      if (requestId !== anchorDataRequestIdRef.current) return;
      const results = res.data.results;
      const defaults = buildDefaultValues(results);

      setCategoryData(results);
      setDefaultValues(defaults);
      methods.reset(defaults);
      setDataLoadId((prev) => prev + 1);
      if (category === ANCHORDATA_VIRTUAL_CATEGORY_MISSING_REQUIRED) {
        missingRequiredRequestIdRef.current += 1;
        setHasMissingRequired(results.length > 0);
      }
    } catch (error) {
      if (requestId !== anchorDataRequestIdRef.current) return;
      handleError(error);
    }
  };

  const loadContractTabs = async (
    subject: string
  ): Promise<ContractSetupTab[]> => {
    const requestId = ++contractTabsRequestIdRef.current;
    const tabs = await new Promise<ContractSetupTab[]>((resolve, reject) => {
      api.genericApiRequest({
        method: "get",
        entity: "anchorDataTemplate",
        additionalRouteParts: { contract_tabs: "/" },
        parametersToRender: { subject },
        successHandler: (response: { data: ContractSetupTab[] }) =>
          resolve(Array.isArray(response.data) ? response.data : []),
        failHandler: reject,
      });
    });
    if (
      requestId === contractTabsRequestIdRef.current &&
      selectionRef.current.subjectId === subject
    ) {
      setContractTabs(tabs);
    }
    return tabs;
  };

  const onSubmit = async (data: any) => {
    if (!subjectId) return;
    const isContractSetup = contractTabs.some((tab) => tab.code === category);
    const allFields: AnchorField[] | any = categoryData?.map((item: any) => ({
      name: item?.name,
      type: item?.type,
      valid_from: item?.anchor_data?.valid_from,
    }));

    const changedValues = getChangedFormFields(
      data,
      methods.formState.defaultValues
    );
    const valuePayload = transformFormValuesToAnchorData(
      changedValues,
      allFields
    );

    const dataToSubmit = [
      {
        subject: subjectId,
        anchor_data_values: valuePayload.anchor_data_values,
      },
    ];
    try {
      const res: any = await new Promise<void>((resolve, reject) => {
        api.genericApiRequest({
          method: "put",
          entity: "anchorData",
          additionalRouteParts: { bulk_create_update: "/" },
          parametersToRender: {},
          submitData: dataToSubmit,
          successHandler: (res: any) => resolve(res),
          failHandler: (error: any) => reject(error),
        });
      });
      const bulkErrors = getAnchorDataBulkCreateUpdateErrors(res, {
        submittedSubjectIds: subjectId ? [subjectId] : [],
      });
      if (bulkErrors.length > 0) {
        throw bulkErrors;
      }
      if (
        selectionRef.current.subjectId !== subjectId ||
        selectionRef.current.category !== category
      ) {
        return;
      }
      const updatedTabs = await loadContractTabs(subjectId);
      if (
        selectionRef.current.subjectId !== subjectId ||
        selectionRef.current.category !== category
      ) {
        return;
      }
      if (
        isContractSetup &&
        !updatedTabs.some((tab) => tab.code === category)
      ) {
        setCategory(false);
      } else {
        await getAnchorData();
      }
      if (category !== ANCHORDATA_VIRTUAL_CATEGORY_MISSING_REQUIRED) {
        await checkMissingRequired(subjectId);
      }

      setNotificationMessage(t("save_successful"));
      setNotificationVariant("success");
      setSuccessForNotification(true);
    } catch (error) {
      handleError(error);
    }
  };

  const checkMissingRequired = async (subject: string) => {
    const requestId = ++missingRequiredRequestIdRef.current;
    try {
      const response = await new Promise<{ data: { results: unknown[] } }>(
        (resolve, reject) => {
          api.genericApiRequest({
            method: "get",
            entity: "anchorDataTemplate",
            parametersToRender: { subject, missing_required: true, limit: 1 },
            successHandler: resolve,
            failHandler: reject,
          });
        }
      );
      if (
        requestId !== missingRequiredRequestIdRef.current ||
        selectionRef.current.subjectId !== subject
      ) {
        return;
      }
      setHasMissingRequired(response.data.results.length > 0);
    } catch (error) {
      if (
        requestId === missingRequiredRequestIdRef.current &&
        selectionRef.current.subjectId === subject
      ) {
        handleError(error);
      }
    }
  };

  const formActionButtonsDisabled = (): boolean => {
    const formValues = methods.watch();
    const changedValues = getChangedFormFields(
      formValues,
      methods.formState.defaultValues
    );

    return Object.keys(changedValues).length === 0;
  };

  const resetStateOnCloseNotification = () => {
    setSuccessForNotification(false);
  };

  const restForm = async () => {
    const confirmReset = await showConfirmationModalAsync(
      t("reset_form_confirm")
    );
    if (!confirmReset) {
      return;
    }
    methods.reset(defaultValues);
  };

  const handleNotification = (
    variant: "success" | "error" | "warning" | "info",
    message: string,
    success: boolean
  ) => {
    setNotificationMessage(message);
    setSuccessForNotification(success);
    setNotificationVariant(variant);
  };

  const exportMenuItems: ExportMenuItem[] = [
    {
      label: "export_all_as",
      format: "csv",
    },
    {
      label: "export_all_as",
      format: "xlsx",
      icon: "chart",
    },
  ];

  return (
    <div className={styles.anchorDataListWrapper}>
      <FormFieldLabel label={t("company")} htmlFor="anchor-data-company" />
      <div className={styles.anchorDataTopWrapper}>
        <div className={styles.subjectSearchField}>
          {isResolvingCompany ? (
            <LinearProgress aria-label={t("loading")} />
          ) : (
            <DropDownSubjectSearch
              name="anchor-data-company"
              placeholder={t("company")}
              subdomain={subdomain}
              options={[]}
              onChange={handleCompanySelect}
              queryParameter="companies"
              additionalSearchParameters={{ include_draft: "true" }}
              resetInput={handleSubjectReset}
              autofocus={true}
              customRef={anchorDataSubjectRef}
              useGlobalState={true}
              isClearable={true}
              alignHeight={true}
            />
          )}
        </div>

        <CompaniesMissingRequiredButton
          onSelectCompany={handleMissingRequiredCompanySelect}
        />

        <ExportMenu
          entity="anchorData"
          exportParameter="export_xlsx"
          handleError={handleError}
          handleNotification={handleNotification}
          showAdditionalAnchordataCheckbox={true}
          menuItems={exportMenuItems}
          buttonClassname={styles.exportButton}
        />
      </div>
      {subjectId && (
        <>
          <div className={styles.anchorDataTopControls}>
            <CategoryTabPanel
              category={category}
              onChange={handleCategoryChange}
              contractTabs={contractTabs}
              showMissingRequiredTab={
                hasMissingRequired === true ||
                (hasMissingRequired === null &&
                  category === ANCHORDATA_VIRTUAL_CATEGORY_MISSING_REQUIRED)
              }
            />

            {category && (
              <>
                <InputWithRef
                  className={styles.searchInput}
                  clearable={true}
                  name="anchorData_inputField_search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("input_search_for", {
                    name: t("input_search_name"),
                    label: t("input_search_label"),
                    value: searchForValues
                      ? `${t("and")} ${t("input_search_value")}`
                      : "",
                  })}
                />
                <FormControlLabel
                  className={styles.search_checkbox}
                  control={
                    <Checkbox
                      checked={searchForValues}
                      onChange={(e) => setSearchForValues(e.target.checked)}
                    />
                  }
                  label={t("anchordata_search_for_values")}
                />
              </>
            )}
          </div>
          {defaultValues && (
            <FormProvider {...methods}>
              {category && (
                <form
                  key={`${subjectId}-${category}-${dataLoadId}`}
                  onSubmit={methods.handleSubmit(onSubmit)}
                  className={styles.form}
                  noValidate>
                  <div className={styles.anachorDataFormScrollArea}>
                    {category ===
                      ANCHORDATA_VIRTUAL_CATEGORY_MISSING_REQUIRED &&
                    categoryData !== null &&
                    categoryData.length === 0 ? (
                      <p className={styles.missingRequiredEmpty}>
                        {t("anchordata_missing_required_empty")}
                      </p>
                    ) : (
                      <GenericAccordion
                        items={accordionItems}
                        openIndices={openAccordionIndices}
                      />
                    )}
                  </div>

                  <div className={styles.anchorDataFormButtonBar}>
                    <GenericButton
                      type="button"
                      variant="outlined"
                      onClick={restForm}
                      disabled={formActionButtonsDisabled()}>
                      {t("reset")}
                    </GenericButton>
                    <GenericButton
                      variant="contained"
                      type="submit"
                      disabled={formActionButtonsDisabled()}>
                      {t("save")}
                    </GenericButton>
                  </div>
                </form>
              )}
            </FormProvider>
          )}
        </>
      )}
      {errorModal}

      <GenericNotification
        message={successForNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </div>
  );
};

//helperfunctions to check and get dirty fields
function checkForEmptyValue(value: any): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  )
    return true;

  return false;
}

function getChangedFormFields(
  currentValues: Record<string, any>,
  initialValues: Record<string, any> | any
): Record<string, any> {
  const changed: Record<string, any> = {};

  Object.keys(currentValues).forEach((key) => {
    const current = currentValues[key];
    const initial = initialValues[key];

    const bothEmpty =
      checkForEmptyValue(current) && checkForEmptyValue(initial);
    const valuesEqual = JSON.stringify(current) === JSON.stringify(initial);

    if (!bothEmpty && !valuesEqual) {
      changed[key] = current;
    }
  });

  return changed;
}

//helperfunction to transform formDataValues for backend
function transformFormValuesToAnchorData(
  formValues: Record<string, any>,
  fields: AnchorField[]
) {
  const anchorDataValues: Record<string, any> = {};

  fields.forEach((field) => {
    let fieldValue = formValues[field.name];

    if (fieldValue !== undefined || fieldValue === null) {
      if (
        Array.isArray(fieldValue) &&
        fieldValue.every((v) => v && "value" in v)
      ) {
        fieldValue = fieldValue.map((v) => v.value);
      }
      anchorDataValues[field.name] = {
        value: fieldValue,
      };

      if (fieldValue === null) {
        anchorDataValues[field.name] = null;
      }

      if (field.valid_from) {
        anchorDataValues[field.name].valid_from = field.valid_from;
      }
    }
  });

  return {
    anchor_data_values: anchorDataValues,
  };
}
//builds the RHF default values from the fetched category data
function buildDefaultValues(categoryData: any[]): Record<string, any> {
  const defaults: any = {};

  categoryData.forEach((item: any) => {
    const name = item?.name;
    if (!name) return;

    const normalValue = item?.anchor_data?.value;
    const inheritedValue = item?.anchor_data_inherited?.value;

    const rawValue = normalValue ?? inheritedValue ?? "";

    switch (item.type) {
      case "date":
        defaults[name] = normalizeDate(rawValue);
        break;

      default:
        defaults[name] = rawValue;
    }
  });

  return defaults;
}

function normalizeDate(rawValue: string | null): Dayjs | null {
  if (!rawValue) return null;
  return dayjs(rawValue).local();
}

export default AnchorDataList;
